import { useEffect, useState } from "react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Alert } from "react-bootstrap";
import { addClients, getClients, resetStore } from "../slices/clientReducer";
import { PAGE_PERMISSIONS } from "../constants/user";
import Base from "./Base";
import useCheckAuthorization from "../hooks/checkAuthorization";
import {
  createFacilitation,
  getFacilitation,
  resetFacilitationState,
} from "../slices/facilitationReducer";
import { GrUserSettings } from "react-icons/gr";
import { FiUsers } from "react-icons/fi";
import { BiTransfer } from "react-icons/bi";
import { MdOutlineAnalytics } from "react-icons/md";
import { RiExchangeDollarLine } from "react-icons/ri";
import { AiOutlineClockCircle } from "react-icons/ai";
import Forex from "./Forex/Forex";
import { CREATE_CLIENT_FIELDS, CREATE_CLIENT_SCHEMA } from "../constants/clients";
import CreateNewClient from "./clients/CreateNewClient";
import AddFacilitation from "./ExportImportFacilitation/AddFacilitation";
import { FACILITATION_CREATION_FIELDS } from "../constants/facilitations";
import { getDashboardDetails } from './DashboardHelper'
import toast from "react-hot-toast";
import { truncateText } from "../utils";
import { extractErrorMessage } from "../constants/common";

function Dashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isForexOffcanvasOpen, setIsForexOffcanvasOpen] = useState(false);
  const [showAddClient, SetAddClient] = useState(false);
  const [showAddFacilitation, SetAddFacilitation] = useState(false);
  const [errors, setErrors] = useState({});
  const [updateData, setValues] = useState({});
  const [current_page, setCurrentPage] = useState(1);
  const { loading, error, success, successMsg } = useSelector((state) => state.clients);
  const { name } = useSelector((state) => state.common);
  const { loading: facilitationLoading, error: facilitationError, success: facilitationSuccess, successMsg: facilitationSuccessMsg } =
    useSelector((state) => state.facilitation);

  const [dashboardData, setDashboardData] = useState({
    users: { total: 0, pending: 0, assigned: 0 },
    clients: { total: 0, pending: 0 },
    facilitation: { total: 0 },
    hsn: { total: 0, pending: 0 },
    sac: { total: 0, pending: 0 },
    forex: { total: 0 },
    pendingClients: { total: 0, active: 0 }
  });

  useEffect(() => {
    dispatch(getClients(1));
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const result = await getDashboardDetails();
      if (result.type === "success" && result.data) {
        setDashboardData(result.data);
      } else {
        console.error("Failed to load dashboard data", result.message);
      }
    } catch (error) {
      console.error("Dashboard API error:", error);
    }
  };

  const statement3Click = () => navigate("/statement3");
  const userFuncClick = () => navigate("/user-management");
  const clientsClick = () => navigate("/clients");
  const FacilitationClick = () => {
    dispatch(getFacilitation({}));
    navigate("/Export-Import-Facilitation");
  };
  const HsnSacClick = () => navigate("/hsn/sac");
  const addClientClick = () => navigate("/client-management");
  const addFacilitationClick = () => SetAddFacilitation(true);
  const handleAddFacilitationClose = () => SetAddFacilitation(false);
  const addForexClick = () => setIsForexOffcanvasOpen(true);
  const pendingClick = () => navigate("/pending-clients");

  const isCheckboxList = (element) => {
    return (
      element instanceof NodeList &&
      element.length > 0 &&
      element[0] instanceof HTMLInputElement &&
      element[0].type === 'checkbox'
    );
  };

  const onAddClientSubmit = async (e, isUpdate = false) => {
    e.preventDefault();
    const values = {};
    Object.keys(CREATE_CLIENT_FIELDS).forEach(field => {
      if (e.target[field].type === 'checkbox') {
        values[field] = e.target[field].checked;
      } else if (isCheckboxList(e.target[field])) {
        values[field] = Array.from(e.target[field]).filter(checkbox => checkbox.checked).map(checkbox => checkbox.value);
      } else {
        values[field] = e.target[field].value;
      }
    });

    try {
      const data = await CREATE_CLIENT_SCHEMA.validate(values, { abortEarly: false });
      if (!isUpdate) {
        dispatch(addClients({ data, page_number: current_page }));
      }
      setErrors({});
      SetAddClient(false);
    } catch (err) {
      const errData = [];
      err.inner.forEach((e) => {
        errData[e.path] = e.message;
        setErrors(errData);
      });
    }
  };

  const handleAddClientClose = () => SetAddClient(false);

  const onAddFacilitationSubmit = async (e, isUpdate = false, id) => {
    e.preventDefault();
    const name = e.target.name?.value;

    try {
      const payload = { name };
      if (!isUpdate) {
        await dispatch(createFacilitation({ data: payload })).unwrap();
      }
      toast.success("Facilitation created successfully!");
      setErrors({});
      handleAddFacilitationClose();
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      toast.error(errorMessage);
      setErrors(errorMessage);
    }
  };

  const DashboardCard = ({
    title,
    icon,
    iconColor,
    stats,
    onCardClick,
    onAddClick,
    showAddButton = false,
    addButtonText = "Add",
    addButtonIcon = "+"
  }) => (
    <Col xs={12} sm={6} xl={4} className="dashboard-card-col">
      <Card
        className="dashboard-card h-80 shadow-sm border-0"
        onClick={onCardClick}
      >
        <Card.Body className="dashboard-card-body d-flex flex-column justify-content-between ">
          <div className="dashboard-card-header-centered text-center ">
            <div className="dashboard-icon-wrapper mx-auto mb-3 "
              style={{ backgroundColor: iconColor + '20', color: iconColor }}>
              {icon}
            </div>
            <h6 className="dashboard-card-title">{title}</h6>
          </div>
          <hr className="muted-hr" />
          <div className="dashboard-stats-section d-flex justify-content-between align-items-center ">
            {stats.map((stat, index) => (
              <React.Fragment key={index}>
                <div className="dashboard-stat-footer-item text-center">
                  <div className="dashboard-stat-number">{stat.value}</div>
                  <div className="dashboard-stat-label">{stat.label}</div>
                  {stat.isPending && (
                    <div className="dashboard-pending-badge mt-1 bg-soft-danger">
                      Pending {stat.pendingLabel} ({stat.pendingValue})
                    </div>
                  )}
                </div>
                {index < stats.length - 1 && <hr className="vertical-hr" />}
              </React.Fragment>
            ))}
            {showAddButton && (
              <>
                <hr className="vertical-hr" />
                <div className="text-center">
                  <button
                    className="dashboard-add-button relative hover:bg-blue-600 hover:text-white hover:shadow-[0_0_15px_5px_var(--colr-pri)] hover:scale-105 transition duration-300 ease-in-out"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddClick && onAddClick();
                    }}
                  >
                    <span className="dashboard-add-icon">{addButtonIcon}</span>
                    {addButtonText}
                  </button>
                </div>
              </>
            )}

          </div>
        </Card.Body>
      </Card>
    </Col>
  );

  return (
    <Base hideDatepicker={true}>
      {/* {(success || error || facilitationSuccess || facilitationError) && (
        <Row>
          <Col md={12} className="d-flex justify-content-center">
            <Alert
              dismissible
              variant={success || facilitationSuccess ? "success" : "danger"}
              onClose={() => {
                dispatch(resetStore());
                dispatch(resetFacilitationState());
              }}
            >
              <p>
                {success && successMsg}
                {error && error}
                {facilitationSuccess && facilitationSuccessMsg}
                {facilitationError && facilitationError}
              </p>
            </Alert>
          </Col>
        </Row>
      )} */}
      <Container fluid className="dashboard-container">
        <div className="dashboard-welcome-section">
          <h4 className="dashboard-welcome-text">
            Welcome
            <span className="dashboard-user-badges text-[#F37920] rounded-md font-semibold px-2 py-1 p-2">
              {truncateText(name ? name : "", 40)}
            </span>
          </h4>
        </div>

        <Row className="dashboard-cards-row">
          {useCheckAuthorization(PAGE_PERMISSIONS.user) && (
            <DashboardCard
              title="Manage Users"
              icon={<GrUserSettings />}
              iconColor="#3498db"
              onCardClick={userFuncClick}
              stats={[
                { value: dashboardData.users.total, label: "Total User" },
                { value: dashboardData.users.pending, label: "Pending User" },
                { value: dashboardData.users.assigned, label: "Assigned" }
              ]}
            />
          )}

          {useCheckAuthorization(PAGE_PERMISSIONS.client) && (
            <DashboardCard
              title="Clients"
              icon={<FiUsers />}
              iconColor="#e74c3c"
              onCardClick={clientsClick}
              showAddButton={true}
              addButtonText="Manage Clients"
              addButtonIcon="📁"
              onAddClick={addClientClick}
              stats={[
                { value: dashboardData.clients.total, label: "Total" },
                { value: dashboardData.clients.pending, label: "Pending" }
              ]}
            />
          )}
          {useCheckAuthorization(PAGE_PERMISSIONS.facilitation) && (
            <DashboardCard
              title="Export Import Facilitation"
              icon={<BiTransfer />}
              iconColor="#2ecc71"
              onCardClick={FacilitationClick}
              showAddButton={true}
              addButtonText="Add Facilitation"
              addButtonIcon="⚙️"
              onAddClick={addFacilitationClick}
              stats={[
                { value: dashboardData.facilitation.total, label: "Total Facilitation" }
              ]}
            />
          )}
          {useCheckAuthorization(PAGE_PERMISSIONS.hsn) && (
            <DashboardCard
              title="HSN/SAC"
              icon={<MdOutlineAnalytics />}
              iconColor="#f39c12"
              onCardClick={HsnSacClick}
              stats={[
                { value: dashboardData.hsn.total, label: "Total HSN", isPending: true, pendingLabel: "HSN", pendingValue: dashboardData.hsn.pending },
                { value: dashboardData.sac.total, label: "Total SAC", isPending: true, pendingLabel: "SAC", pendingValue: dashboardData.sac.pending }
              ]}
            />
          )}
          {useCheckAuthorization(PAGE_PERMISSIONS.forex) && (
            <DashboardCard
              title="Forex"
              icon={<RiExchangeDollarLine />}
              iconColor="#9b59b6"
              showAddButton={true}
              addButtonText="Add Forex Currency"
              addButtonIcon="₹"
              onAddClick={addForexClick}
              stats={[
                { value: dashboardData.forex.total, label: "Total Currency" }
              ]}
            />
          )}
          {useCheckAuthorization(PAGE_PERMISSIONS.client) && (
            <DashboardCard
              title="Pending Clients"
              icon={<AiOutlineClockCircle />}
              iconColor="#e67e22"
              stats={[
                { value: dashboardData.pendingClients.total, label: "Total Pending Clients" },
                { value: dashboardData.pendingClients.active, label: "Active Clients" }
              ]}
              onCardClick={pendingClick}
            />
          )}
        </Row>
      </Container>
      <Forex
        isOffcanvasOpen={isForexOffcanvasOpen}
        setIsOffcanvasOpen={setIsForexOffcanvasOpen}
        showMainContent={false}
      />
      <CreateNewClient
        fields={CREATE_CLIENT_FIELDS}
        handleSubmit={onAddClientSubmit}
        errors={errors}
        show={showAddClient}
        onHideFunc={handleAddClientClose}
        isUpdate={false}
        values={updateData}
      />
      <AddFacilitation
        handleSubmit={onAddFacilitationSubmit}
        fields={FACILITATION_CREATION_FIELDS}
        errors={errors}
        show={showAddFacilitation}
        onHideFunc={handleAddFacilitationClose}
        values={{}}
        isUpdate={false}
        id={{}}
      />
    </Base>
  );
}

export default Dashboard;